import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useDdSites } from "../../../hooks";
import { DdOcdeCap, DdOcdeRr, DdOcdeAe, DdOcdeDd6 } from "../../../api";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { RISKS, RR_DEFAULT, buildDefaultScoringForSite, calcSig, classify, CLASS_LABEL } from "../OcdeRiskRegister/ocdeRrCatalog";
import { DIMENSIONS, MATURITY_LABELS, AE_DEFAULT } from "../SelfAssessments/ocdeAeCatalog";
import { DD6_STAGES, dd6Normalize, dd6StageScore, dd6GlobalScore } from "../OcdeDdProcess/ocdeDd6Catalog";
import { CAP_DEFAULT, STATUS_LABELS, STATUS_PILL } from "../OcdeCap/ocdeCapCatalog";
import "./DashboardDD.scss";

const ddCapApi  = new DdOcdeCap();
const ddRrApi   = new DdOcdeRr();
const ddAeApi   = new DdOcdeAe();
const ddDd6Api  = new DdOcdeDd6();

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

export function DashboardDD() {
  return (
    <div className="ddd-page">
      <DDNav />
      <div className="ddd-content">
        <div className="ddd-ph">
          <div>
            <h1 className="ddd-title">Dashboard de Debida Diligencia OCDE</h1>
            <div className="ddd-sub">Vista consolidada · Etapas 2.1 · 2.2 · 3.1 de la Guía OCDE 2021 para el sector textil y del calzado</div>
          </div>
        </div>
        <DDPanel />
      </div>
    </div>
  );
}

/** Panel OCDE: indicadores, flujo, top riesgos/gaps, cobertura y vencimientos. También va en el Inicio. */
export function DDPanel() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { sites, sitesLoading, sitesError, reloadSites } = useDdSites(accessToken);

  const [rrData, setRrData] = useState(null);
  const [aeData, setAeData] = useState(null);
  const [caps,   setCaps]   = useState([]);
  const [dd6Data, setDd6Data] = useState(() => dd6Normalize({}));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Si algo no carga se avisa, en vez de mostrar los valores de demostración como si fueran de la empresa.
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      ddRrApi.getData(accessToken),
      ddAeApi.getData(accessToken),
      ddCapApi.getData(accessToken),
      ddDd6Api.getData(accessToken),
    ]).then(([rr, ae, capRes, dd6]) => {
      setRrData(rr);
      setAeData(ae);
      setCaps(capRes && Array.isArray(capRes.caps) ? capRes.caps : CAP_DEFAULT);
      setDd6Data(dd6Normalize(dd6?.data));
    })
      .catch((err) => { console.error("Error cargando Dashboard DD", err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [accessToken, reloadKey]);

  // ── Proceso DD · 6 etapas ─────────────────────────────────────────────────
  const dd6Global = dd6GlobalScore(dd6Data);

  // ── Risk Register 2.1 · significance por riesgo OCDE (promedio de sitios) ──
  const rrScoring = rrData?.scoring || RR_DEFAULT;
  const rrStatus  = rrData?.status  || {};
  const sig = RISKS.map((r) => {
    const perSite = sites.length ? sites : [{ id: "ba" }];
    const sigs = perSite.map((st) => calcSig(rrScoring[r.id]?.[st.id] || buildDefaultScoringForSite(r.id)));
    const avg  = sigs.reduce((a, b) => a + b, 0) / sigs.length;
    return { id: r.id, name: r.name, avg, cls: classify(avg) };
  }).filter((r) => r.avg > 5).sort((a, b) => b.avg - a.avg);

  // ── Autoevaluación 2.2 · madurez por dimensión OCDE (promedio de sitios) ──
  const aeMaturity = aeData?.maturity || AE_DEFAULT;
  const dims = DIMENSIONS.map((d) => {
    const perSite = sites.length ? sites : [{ id: "ba" }];
    const avg = perSite.reduce((a, st) => a + (aeMaturity[d.id]?.[st.id]?.level ?? 1), 0) / perSite.length;
    const withEvidence = perSite.every((st) => (aeMaturity[d.id]?.[st.id]?.notes || "").trim().length > 0);
    return { id: d.id, short: d.short, avg, withEvidence };
  });

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const critRisks  = sig.filter(r => r.cls === "crit");
  const highRisks  = sig.filter(r => r.cls === "high");

  const gapDims      = dims.filter(d => d.avg < 3);
  const activeCaps   = caps.filter(c => c.status !== "completado");
  const closedCaps   = caps.filter(c => c.status === "completado");
  const avgProgress  = activeCaps.length
    ? Math.round(activeCaps.reduce((s, c) => s + (c.progress || 0), 0) / activeCaps.length)
    : null;

  // ── Flow ──────────────────────────────────────────────────────────────────
  const rrDone   = Object.values(rrStatus).filter(s => s === "done").length;
  const rrTotal  = RISKS.length;
  const rrSig    = sig.length;

  const dimsWithEvidence = dims.filter(d => d.withEvidence).length;
  const maxDims          = DIMENSIONS.length;

  const capTotal = activeCaps.length + closedCaps.length;

  // ── Top risks ─────────────────────────────────────────────────────────────
  const topRisks = sig.slice(0, 5);

  // ── Top gaps ──────────────────────────────────────────────────────────────
  const topGaps = [...gapDims]
    .sort((a, b) => a.avg - b.avg)
    .slice(0, 5);

  // ── Coverage ──────────────────────────────────────────────────────────────
  const gapIds      = gapDims.map(d => d.id);
  const gapCovered  = gapIds.filter(did => caps.some(c => c.originType === "gap" && c.originRef === did)).length;
  const covGapsPct  = gapIds.length ? Math.round((gapCovered / gapIds.length) * 100) : 0;

  const sigIds      = sig.map(r => r.id);
  const sigCovered  = sigIds.filter(rid => caps.some(c => c.originType === "risk" && c.originRef === rid)).length;
  const covRisksPct = sigIds.length ? Math.round((sigCovered / sigIds.length) * 100) : 0;

  // ── Upcoming CAPs ─────────────────────────────────────────────────────────
  const upcoming = caps
    .filter(c => c.status !== "completado" && c.deadline)
    .map(c => ({ ...c, days: daysUntil(c.deadline) }))
    .filter(c => c.days !== null && c.days <= 30 && c.days >= -90)
    .sort((a, b) => a.days - b.days)
    .slice(0, 8);

  const RISK_COLORS = { crit: "#7F1D1D", high: "#E07020", med: "#F4B83B", low: "#2E9461" };

  if (!loading && !sitesLoading && (loadError || sitesError)) {
    return <DDLoadError bare onRetry={() => { if (loadError) setReloadKey((k) => k + 1); if (sitesError) reloadSites(); }} />;
  }

  if (loading || sitesLoading) return <div className="ddd-loading">Cargando dashboard…</div>;

  return (
    <>
      {/* ── KPI Row ── */}
      <div className="ddd-kpi-row">
        <Kpi label="Riesgos críticos"  value={critRisks.length}  formula="significance ≥ 16"  color="crit" />
        <Kpi label="Riesgos altos"     value={highRisks.length}  formula="significance 11–15" color="o"    />
        <Kpi label="Gaps de madurez"   value={gapDims.length}    formula="dimensiones < nivel 3" color="r" />
        <Kpi label="CAPs activos"      value={activeCaps.length} formula="en curso o planif."  color="purple"/>
        <Kpi label="Avance CAP"        value={avgProgress !== null ? `${avgProgress}%` : "—"} formula="% promedio" color="y" />
        <Kpi label="CAPs completados"  value={closedCaps.length} formula="acciones cerradas"  color="g"    />
      </div>

      {/* ── Flow Chain ── */}
      <div className="ddd-flow">
        <h3>Flujo de Debida Diligencia OCDE · 3 etapas conectadas</h3>
        <div className="ddd-flow-chain">
          <FlowStage
            icon="⚠️" name="Risk Register" ref_="2.1"
            stat={`${rrDone} / ${rrTotal}`}
            meta={`riesgos completos · ${rrSig} significativos`}
            bar={rrTotal > 0 ? (rrDone / rrTotal) * 100 : 0}
            onClick={() => navigate("/admin/ocde-risk-register")}
          />
          <div className="ddd-flow-arrow">→</div>
          <FlowStage
            icon="🏢" name="Autoevaluación" ref_="2.2"
            stat={`${dimsWithEvidence} / ${maxDims}`}
            meta={`dimensiones con evidencia · ${gapDims.length} gaps`}
            bar={maxDims > 0 ? (dimsWithEvidence / maxDims) * 100 : 0}
            onClick={() => navigate("/admin/self-assessments")}
          />
          <div className="ddd-flow-arrow">→</div>
          <FlowStage
            icon="🎯" name="CAP Tracker" ref_="3.1"
            stat={capTotal}
            meta={`CAPs derivados · ${closedCaps.length} completados`}
            bar={capTotal > 0 ? (closedCaps.length / capTotal) * 100 : 0}
            onClick={() => navigate("/admin/ocde-cap")}
          />
        </div>
      </div>

      {/* ── Proceso DD · 6 etapas ── */}
      <div className="ddd-card ddd-dd6">
        <div className="ddd-card-hd">
          <h3>Proceso DD · 6 etapas · avance global {dd6Global.avgPct}%</h3>
          <button className="ddd-btn-sm" onClick={() => navigate("/admin/ocde-dd-process")}>
            {dd6Global.stagesDone}/{dd6Global.total} etapas evaluadas · Abrir →
          </button>
        </div>
        <div className="ddd-card-body ddd-dd6-grid">
          {DD6_STAGES.map((st) => {
            const sc = dd6StageScore(st, dd6Data);
            return (
              <div key={st.id} className="ddd-dd6-row" onClick={() => navigate("/admin/ocde-dd-process")} title={st.title}>
                <div className="ddd-dd6-tx"><span>{st.n}. {st.title}</span><b>{sc.pct}%</b></div>
                <div className="ddd-dd6-bar"><div style={{ width: `${sc.pct}%`, background: st.color }} /></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Top Risks + Top Gaps ── */}
      <div className="ddd-grid-2">
        <div className="ddd-card">
          <div className="ddd-card-hd">
            <h3>Top riesgos significativos</h3>
            <span className="ddd-card-meta">desde Risk Register 2.1</span>
          </div>
          <div className="ddd-card-body">
            {topRisks.length === 0 ? (
              <p className="ddd-empty">Sin riesgos significativos registrados</p>
            ) : (
              <ul className="ddd-top-list">
                {topRisks.map((r, i) => (
                  <li key={r.id || i} className="ddd-top-item" onClick={() => navigate("/admin/ocde-risk-register")}>
                    <span className="ddd-top-num">{i + 1}</span>
                    <span className="ddd-top-nm">
                      <b>{r.name}</b>
                      <small>significance promedio: {r.avg.toFixed(2)}</small>
                    </span>
                    <span
                      className="ddd-top-badge"
                      style={{ background: (RISK_COLORS[r.cls] || "#9CA3AF") + "22", color: RISK_COLORS[r.cls] }}
                      title={CLASS_LABEL[r.cls]}
                    >
                      {r.avg.toFixed(1)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="ddd-card">
          <div className="ddd-card-hd">
            <h3>Top gaps de madurez</h3>
            <span className="ddd-card-meta">desde Autoevaluación 2.2</span>
          </div>
          <div className="ddd-card-body">
            {topGaps.length === 0 ? (
              <p className="ddd-empty">Sin gaps registrados</p>
            ) : (
              <ul className="ddd-top-list">
                {topGaps.map((d, i) => (
                  <li key={d.id} className="ddd-top-item" onClick={() => navigate("/admin/self-assessments")}>
                    <span className="ddd-top-num">{i + 1}</span>
                    <span className="ddd-top-nm">
                      <b>{d.short}</b>
                      <small>madurez {Number(d.avg.toFixed(1))} · {MATURITY_LABELS[Math.round(d.avg)] || ""}</small>
                    </span>
                    <span className="ddd-top-badge" style={{ background: "#FBE1CC", color: "#9A4A0E" }}>
                      {Number(d.avg.toFixed(1))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Coverage ── */}
      <div className="ddd-formwrap">
        <h4>
          Cobertura del Plan de Acción
          <span className="ddd-h-meta">qué fracción de riesgos significativos y gaps tiene al menos un CAP asociado</span>
        </h4>
        <div className="ddd-coverage-grid">
          <CoverageCard
            title="Riesgos significativos cubiertos por CAPs"
            pct={covRisksPct}
            meta={`${sigCovered} de ${sigIds.length} riesgos significativos con al menos un CAP asociado`}
          />
          <CoverageCard
            title="Gaps de madurez cubiertos por CAPs"
            pct={covGapsPct}
            meta={`${gapCovered} de ${gapIds.length} dimensiones con gaps cubiertas por al menos un CAP`}
          />
        </div>
      </div>

      {/* ── Upcoming CAPs ── */}
      <div className="ddd-listcard">
        <div className="ddd-listhd">
          <div>
            <h3>CAPs próximos a vencer · 30 días</h3>
            <div className="ddd-lh-meta">{upcoming.length} vencimientos en los próximos 30 días o vencidos</div>
          </div>
          <button className="ddd-btn-sm" onClick={() => navigate("/admin/ocde-cap")}>
            Ver todos los CAPs →
          </button>
        </div>
        {upcoming.length === 0 ? (
          <div className="ddd-empty ddd-empty--pad">Sin vencimientos próximos</div>
        ) : (
          <table className="ddd-upc-table">
            <thead>
              <tr><th>CAP</th><th>Deadline</th><th>Avance</th><th>Estado</th></tr>
            </thead>
            <tbody>
              {upcoming.map((c, i) => {
                const tone = c.days < 0 ? "overdue" : c.days <= 7 ? "urgent" : "";
                return (
                  <tr key={c.id || i}>
                    <td>
                      <span className="ddd-upc-code">{c.id}</span>
                      <b className="ddd-upc-title">{c.title}</b>
                    </td>
                    <td className={`ddd-upc-date ${tone}`}>
                      <b>{c.deadline}</b>
                      <small>{c.days < 0 ? `${-c.days} días vencido` : c.days === 0 ? "Hoy" : `en ${c.days} días`}</small>
                    </td>
                    <td>
                      <div className="ddd-upc-prog">
                        <div className="ddd-upc-prog-bar">
                          <div className={`ddd-upc-prog-fill ${(c.progress || 0) >= 40 ? "mid" : "low"}`} style={{ width: `${c.progress || 0}%` }} />
                        </div>
                        <span>{c.progress || 0}%</span>
                      </div>
                    </td>
                    <td><span className={`ddd-upc-pill ${STATUS_PILL[c.status] || "muted"}`}>{STATUS_LABELS[c.status] || c.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Kpi({ label, value, formula, color }) {
  return (
    <div className={`ddd-kpi ddd-kpi--${color}`}>
      <div className="ddd-kpi__lbl">{label}</div>
      <div className="ddd-kpi__val">{value ?? "—"}</div>
      <div className="ddd-kpi__frml">{formula}</div>
    </div>
  );
}

function FlowStage({ icon, name, ref_, stat, meta, bar, onClick }) {
  return (
    <div className="ddd-flow-stage" onClick={onClick}>
      <div className="ddd-flow-stage__head">
        <span className="ddd-flow-stage__ico">{icon}</span>
        <span className="ddd-flow-stage__nm">{name}</span>
        <span className="ddd-flow-stage__ref">{ref_}</span>
      </div>
      <div className="ddd-flow-stage__stat">{stat ?? "—"}</div>
      <div className="ddd-flow-stage__meta">{meta}</div>
      <div className="ddd-flow-stage__bar" style={{ width: `${Math.min(bar, 100)}%` }} />
    </div>
  );
}

function CoverageCard({ title, pct, meta }) {
  const cls = pct >= 80 ? "good" : pct >= 50 ? "warn" : "";
  return (
    <div className="ddd-cov-card">
      <div className="ddd-cov-card__ttl">{title}</div>
      <div className={`ddd-cov-card__pct ${cls}`}>{pct}%</div>
      <div className="ddd-cov-card__meta">{meta}</div>
      <div className="ddd-cov-bar">
        <div className={`ddd-cov-bar__fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
